import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spin, message } from "antd";
import { StoreOrderApi } from "./StoreOrderApi";
import type { PreparePayResult, QueryPayStatusResult } from "./StoreOrderType";
import { useRelatedSpacesOrdersModals } from "../shell/RelatedSpacesOrdersModalsProvider";
import "./CheckoutPage.css";

function aggregatorDisplayLabel(code: string): string {
  const u = code.trim().toLowerCase();
  if (u === "lakala") return "拉卡拉";
  if (u === "shouqianba") return "收钱吧";
  return code || "聚合支付";
}

export default function CheckoutPage() {
  const { spaceId, orderId } = useParams<{ spaceId: string; orderId: string }>();
  const navigate = useNavigate();
  const { openOrdersSpace } = useRelatedSpacesOrdersModals();
  const [prep, setPrep] = useState<PreparePayResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState(false);
  const [payTime, setPayTime] = useState("");
  const [countdown, setCountdown] = useState(10);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goOrders = useCallback(() => {
    if (spaceId) {
      openOrdersSpace({ spaceId });
    } else {
      openOrdersSpace();
    }
  }, [spaceId, openOrdersSpace]);

  useEffect(() => {
    if (!spaceId || !orderId) {
      message.error("参数缺失");
      navigate("/");
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await StoreOrderApi.preparePay({
        spaceId,
        orderId,
        payScene: "web_page",
        returnUrl: typeof window !== "undefined" ? window.location.href : undefined,
        clientUserAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      });
      if (cancelled) return;
      if (typeof res === "string") {
        setLoading(false);
        return;
      }
      setPrep(res);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceId, orderId, navigate]);

  useEffect(() => {
    if (!spaceId || !orderId || !prep) return;
    pollRef.current = setInterval(() => {
      void (async () => {
        const res = await StoreOrderApi.queryPayStatus({ spaceId, orderId });
        if (typeof res === "string") return;
        const r = res as QueryPayStatusResult;
        if (r.paid) {
          if (pollRef.current) clearInterval(pollRef.current);
          setPaid(true);
          setPayTime(r.payTime);
        }
      })();
    }, 1500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [spaceId, orderId, prep]);

  useEffect(() => {
    if (!paid) return;
    setCountdown(10);
    cdRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          if (cdRef.current) clearInterval(cdRef.current);
          goOrders();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      if (cdRef.current) clearInterval(cdRef.current);
    };
  }, [paid, goOrders]);

  if (!spaceId || !orderId) {
    return null;
  }

  const aggLabel = aggregatorDisplayLabel(prep?.aggregatorCode ?? "");

  const redirectReady = Boolean(prep?.redirectUrl?.trim());

  let hint = "正在准备收银信息";
  if (redirectReady) {
    hint = "请点击按钮前往聚合收银台完成付款";
  }

  return (
    <div className="checkout-page">
      <div className="checkout-inner">
        <h1 className="checkout-title">收银台</h1>
        <Spin spinning={loading}>
          {prep && !paid ? (
            <div className="checkout-body">
              <p className="checkout-meta">
                {prep.productTypeLabel} · {prep.serviceTierLabel} · ¥
                {prep.payAmountYuan.toFixed(2)}
                {prep.aggregatorCode ? ` · ${aggLabel}` : ""}
              </p>
              {redirectReady ? (
                <Button
                  type="primary"
                  className="checkout-btn"
                  style={{ minWidth: 120 }}
                  href={prep.redirectUrl!.trim()}
                >
                  前往支付
                </Button>
              ) : null}
              <p className="checkout-hint">{hint}</p>
            </div>
          ) : null}
          {paid ? (
            <div className="checkout-done">
              <p>支付成功</p>
              {payTime ? <p className="checkout-time">{payTime}</p> : null}
              <p className="checkout-count">{countdown} 秒后跳转到我的订单</p>
              <Button
                type="primary"
                className="checkout-btn"
                style={{ minWidth: 120 }}
                onClick={goOrders}
              >
                前往订单列表
              </Button>
            </div>
          ) : null}
        </Spin>
      </div>
    </div>
  );
}
